import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import AppService from '../../../Configurations/Appservice';
import { useDispatch } from 'react-redux';
import {
  updateScreenTitle,
  showLoader,
  dismissLoader,
} from '../../../LayoutContainers/AppLayout/redux/AppSlice';

const RequiredDocumentsPage = () => {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(updateScreenTitle('Required Documents'));
    loadRequiredDocuments();
    return () => dispatch(updateScreenTitle(''));
  }, []);

  const loadRequiredDocuments = () => {
    setLoading(true);
    dispatch(showLoader());
    AppService.get('api/business-categories/my-required-documents')
      .then((response) => {
        setDocuments(response || []);
      })
      .catch((error) => {
        console.error('Failed to load required documents', error);
      })
      .finally(() => {
        setLoading(false);
        dispatch(dismissLoader());
      });
  };

  useEffect(() => {
    let filtered = documents;
    if (searchTerm) {
      filtered = filtered.filter((doc) => {
        const dt = doc.documentType || {};
        return (
          (dt.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (dt.code || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    setFilteredDocuments(filtered);
  }, [searchTerm, documents]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            Required Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Documents required for your organization based on business category
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Authority</TableCell>
              <TableCell align="center">Mandatory</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {documents.length === 0
                      ? 'No required documents configured for your organization'
                      : 'No documents match your search'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc, index) => {
                const dt = doc.documentType || {};
                return (
                  <TableRow key={dt.id || index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {dt.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{dt.name}</Typography>
                        {dt.nameAr && (
                          <Typography variant="caption" color="text.secondary" dir="rtl">
                            {dt.nameAr}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {dt.categoryName ? (
                        <Chip label={dt.categoryName} size="small" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dt.issuingAuthority || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={doc.isMandatory ? 'Yes' : 'No'}
                        size="small"
                        color={doc.isMandatory ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filteredDocuments.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total: {filteredDocuments.length} documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mandatory: {filteredDocuments.filter((d) => d.isMandatory).length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RequiredDocumentsPage;
